import "./slideshow.scss";
import {
    Component,
    type CSSProperties,
    type DetailedHTMLProps,
    type ReactNode,
    type VideoHTMLAttributes,
} from "react";
import { type ValueOf } from "../../../types/internal/utility";
import { cache } from "../../Utilities/Platform/CDN";

export interface SlideshowSlideType {
    type: "VIDEO" | "IMG";
    duration: number;
    url: string;
}

type Props = {
    media: Array<SlideshowSlideType>;
    style: CSSProperties;
    fill?: boolean;
    cover?: boolean;
};

export const STATUS = {
    play : "PLAY",
    pause: "PAUSE",
};

type State = {
    status: ValueOf<typeof STATUS>;
    current: number;
    playNext: boolean;
};

type VideoProps = DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>;

export default class Slideshow extends Component<Props, State> {
    public state: State = {
        status  : STATUS.pause,
        current : -1,
        playNext: true,
    };

    public mediaRefs: Array<null | HTMLVideoElement | HTMLImageElement> = [];

    public timeout: null | number = null;

    public componentDidMount(): void {
        this.play(true);
    }

    public componentWillUnmount(): void {
        if(this.timeout !== null) {
            clearTimeout(this.timeout);
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.media !== this.props.media) {
            this.restart();
        }
    }

    public restart = (): void => {
        if(this.timeout !== null) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        this.setState({
            status  : STATUS.pause,
            current : -1,
            playNext: true,
        }, () => {
            this.play(true);
        });
    };

    public play = (first = false): void => {
        if(!this.props.media.length) {
            return;
        }

        if(this.state.status === STATUS.play) {
            return;
        }

        this.setState({
            status: STATUS.play,
        });

        if(this.state.playNext) {
            this.prepareNext(first);
        } else {
            const ref = this.mediaRefs[this.state.current];

            if(
                this.props.media[this.state.current].type === "VIDEO" &&
                ref instanceof HTMLVideoElement
            ) {
                ref.pause();
            }
        }
    };

    public pause = (): void => {
        if(!this.props.media.length) {
            return;
        }

        if(this.state.status === STATUS.pause) {
            return;
        }

        this.setState({
            status: STATUS.pause,
        });

        const ref = this.mediaRefs[this.state.current];

        if(
            this.props.media[this.state.current].type === "VIDEO" &&
            ref instanceof HTMLVideoElement
        ) {
            ref.pause();
        }
    };

    public prepareNext = (first: boolean): void => {
        if(!this.props.media.length) {
            return;
        }

        if(this.state.status === STATUS.pause && !first) {
            this.setState({
                playNext: true,
            });
        } else {
            const length = this.props.media.length;
            let current = this.state.current;

            current++;

            if(current >= length) {
                current = 0;
            }

            this.setState({
                current,
            });

            const type = this.props.media[current].type;

            if(type === "VIDEO") {
                const ref = this.mediaRefs[current];

                if(ref instanceof HTMLVideoElement) {
                    ref.play();
                }
            } else if(type === "IMG") {
                this.timeout = setTimeout(this.prepareNext, this.props.media[current].duration * 1000);
            }
        }
    };

    public render(): ReactNode {
        const classes = [ "slideshow" ];

        if(this.props.fill) {
            classes.push("fill");
        }

        if(this.props.cover) {
            classes.push("cover");
        }

        return (
            <div className={classes.join(" ")} style={this.props.style}>
                {this.props.media.map((media, index) => {
                    const current = this.state.current === index;
                    let item = null;

                    let className = "media";

                    if(current) {
                        className += " current";
                    }

                    if(media.type === "IMG") {
                        item = (
                            <img
                                key={index}
                                ref={(ref): void => {
                                    this.mediaRefs[index] = ref;
                                }}
                                src={cache(media.url, "media")}
                                className={className}
                                alt="slideshow-image"
                            />
                        );
                    } else if(media.type === "VIDEO") {
                        let tags: VideoProps = {
                            src     : cache(media.url, "media"),
                            muted   : true,
                            controls: false,
                            preload : "auto",
                        };

                        if(current) {
                            tags = {
                                ...tags,
                                onEnded: (): void => {
                                    this.prepareNext(false);
                                },
                            };
                        }

                        item = (
                            <video
                                key={index}
                                ref={(ref): void => {
                                    this.mediaRefs[index] = ref;
                                }}
                                className={className}
                                {...tags}
                            />
                        );
                    }

                    return item;
                })}
            </div>
        );
    }
}
